import type { PackagingPlanRow, Week39PackagingLive, Week39PackagingSnapshot } from "./packaging-report-types.js";
import { displayPlanAllocations } from "./packaging-day-plan.js";
import { productionLineFromDocument, UNMAPPED_PRODUCTION_LINE } from "./packaging-production-routing.js";
import { isManualPackagingProductionDocument } from "./production-document-lines.js";
import { isPackagingWorkOrderPending, packagingWorkOrderKey } from "./packaging-work-order.js";

export type QuantityResult = { value: number | null; unit: string | null; reason: string | null };

function istanbulDate(value: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul", year: "numeric", month: "2-digit", day: "2-digit" }).format(value);
}

export function weekObservationDate(snapshot: Week39PackagingSnapshot, live: Week39PackagingLive | null): string {
  // The ISO week includes Sunday even when Excel only contains six planned days.
  const end = new Date(`${snapshot.weekStart}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  const weekEnd = end.toISOString().slice(0, 10);
  return live && live.range.to < weekEnd ? live.range.to : weekEnd;
}

export function excelRowPlanQuantity(row: PackagingPlanRow): QuantityResult {
  if (row.carryover) return row.carryover.quantity === null
    ? { value: null, unit: null, reason: row.carryover.reason ?? "Devreden hafta bakiyesi doğrulanamadı" }
    : { value: row.carryover.quantity, unit: "KOLİ", reason: null };
  if (row.allocations.some((a) => !Number.isFinite(a.quantity) || a.quantity < 0)) {
    return { value: null, unit: null, reason: "Excel plan miktarı geçersiz" };
  }
  return { value: row.allocations.reduce((sum, allocation) => sum + allocation.quantity, 0), unit: "KOLİ", reason: null };
}

export function excelRowDayPlanQuantity(row: PackagingPlanRow, date: string): QuantityResult {
  if (row.carryover) return { value: null, unit: null, reason: "Devreden işin günlük planı yok" };
  const allocations = displayPlanAllocations(row).filter((allocation) => allocation.date === date);
  if (allocations.some((a) => !Number.isFinite(a.quantity) || a.quantity < 0)) {
    return { value: null, unit: null, reason: "Excel günlük plan miktarı geçersiz" };
  }
  return { value: allocations.reduce((sum, allocation) => sum + allocation.quantity, 0), unit: "KOLİ",
    reason: allocations.some((allocation) => allocation.sourceDate !== date) ? "Excel'deki 1 KOLİ ve altı plan parçası önceki günden birleştirildi" : null };
}

export function weekPlanProgress(actual: QuantityResult, weekPlan: QuantityResult) {
  if (actual.value === null || weekPlan.value === null || weekPlan.value <= 0) return null;
  const percentage = Math.round((actual.value / weekPlan.value) * 100);
  return { percentage, fraction: Math.min(1, Math.max(0, actual.value / weekPlan.value)), met: actual.value >= weekPlan.value };
}

export function weekRemainingQuantity(weekPlan: QuantityResult, weeklyActual: QuantityResult): QuantityResult {
  if (weekPlan.value === null) return { value: null, unit: null, reason: weekPlan.reason ?? "Hafta planı doğrulanamadı" };
  if (weeklyActual.value === null) return { value: null, unit: null, reason: weeklyActual.reason ?? "Mikro verisi alınamadı" };
  if (weekPlan.unit !== "KOLİ" || weeklyActual.unit !== "KOLİ") {
    return { value: null, unit: null, reason: "Kalan miktar için birim KOLİ olarak doğrulanamadı" };
  }
  return { value: Math.max(0, weekPlan.value - weeklyActual.value), unit: "KOLİ", reason: null };
}

export function actualQuantity(snapshot: Week39PackagingSnapshot, row: PackagingPlanRow, day: string,
  live: Week39PackagingLive | null, period: "day" | "week"): QuantityResult {
  if (isPackagingWorkOrderPending(row)) return { value: null, unit: null, reason: "İş emri bekleniyor" };
  if (!live) return { value: null, unit: null, reason: "Mikro verisi alınamadı" };
  if (!live.range?.from || !live.range?.to || live.range.from > live.range.to) {
    return { value: null, unit: null, reason: "Mikro sorgu tarih aralığı geçersiz" };
  }
  if (day < live.range.from || (period === "week" && (live.range.from > snapshot.weekStart || day < snapshot.weekStart))) {
    return { value: null, unit: null, reason: period === "week" ? "Mikro sorgusu hafta başlangıcını kapsamıyor" : "Mikro sorgusu bu günü kapsamıyor" };
  }
  if (day > live.range.to) {
    const observedAt = new Date(live.observedAt ?? Date.now());
    const today = istanbulDate(Number.isNaN(observedAt.getTime()) ? new Date() : observedAt);
    return { value: null, unit: null, reason: day > today ? "Plan günü henüz gelmedi" : "Mikro sorgusu bu günü kapsamıyor" };
  }
  if (row.carryover) {
    if (row.carryover.actualsByDate === null) {
      return { value: null, unit: null, reason: row.carryover.weeklyActualReason ?? "Devreden işin bu hafta üretimi doğrulanamadı" };
    }
    const actuals = row.carryover.actualsByDate.filter((item) => item.date <= day && (period === "week" || item.date === day));
    return { value: actuals.reduce((sum, item) => sum + item.quantity, 0), unit: "KOLİ", reason: null };
  }
  const candidates = live.items.filter((item) => item.workOrderNo === row.workOrderNo && item.productCode === row.productCode
    && item.date >= snapshot.weekStart && item.date <= day && (period === "week" || item.date === day));
  if (candidates.some((item) => productionLineFromDocument(item.documentNo) === null)) {
    return { value: null, unit: null, reason: "Mikro belgesinin hattı doğrulanamadı" };
  }
  const movements = candidates.filter((item) => isManualPackagingProductionDocument(item.documentNo)
    && (!row.productionLine || (productionLineFromDocument(item.documentNo) ?? UNMAPPED_PRODUCTION_LINE) === row.productionLine));
  if (!movements.length && live.workOrders?.find((order) => order.workOrderNo === row.workOrderNo)?.found === false) {
    return { value: null, unit: null, reason: "İş emri Mikro'da bulunamadı" };
  }
  if (movements.some((item) => !Number.isFinite(item.quantity))) {
    return { value: null, unit: null, reason: "Mikro hareket miktarı geçersiz" };
  }
  const units = [...new Set(movements.map((item) => item.unit?.trim().toLocaleUpperCase("tr-TR") ?? null))];
  if (units.some((unit) => !unit) || units.length > 1 || (units.length === 1 && units[0]?.toLocaleUpperCase("tr-TR") !== "KOLİ")) {
    return { value: null, unit: null, reason: "Mikro hareketlerinin birimi KOLİ olarak doğrulanamadı" };
  }
  return { value: movements.reduce((sum, item) => sum + item.quantity, 0), unit: "KOLİ", reason: null };
}

export function sharedRowActuals(snapshot: Week39PackagingSnapshot, row: PackagingPlanRow, day: string,
  live: Week39PackagingLive | null): { daily: QuantityResult; cumulative: QuantityResult } {
  // Carryover rows already hold their exact per-date allocation from the prior-week
  // balance calculation. Re-running the shared-pair allocator after physical-line
  // routing would assign that same output to every synthesized source row.
  if (row.carryover) {
    return {
      daily: actualQuantity(snapshot, row, day, live, "day"),
      cumulative: actualQuantity(snapshot, row, weekObservationDate(snapshot, live), live, "week"),
    };
  }
  const rows = snapshot.lines.flatMap((line) => line.rows)
    .filter((candidate) => packagingWorkOrderKey(candidate) === packagingWorkOrderKey(row))
    .filter((candidate) => candidate.productionLine === row.productionLine)
    .sort((a, b) => (a.excelSourceRow ?? a.sourceRow) - (b.excelSourceRow ?? b.sourceRow) || a.sourceRow - b.sourceRow);
  if (rows.some((candidate) => excelRowPlanQuantity(candidate).value === null)) {
    const invalid = { value: null, unit: null, reason: "Ortak iş emrinde Excel plan miktarı geçersiz" };
    return { daily: invalid, cumulative: invalid };
  }
  const index = rows.findIndex((candidate) => candidate.sourceRow === row.sourceRow);
  if (row.productionLine) return productionDayActuals(snapshot, row, rows, index, day, live);
  const preceding = rows.slice(0, index).reduce((sum, candidate) => sum + (excelRowPlanQuantity(candidate).value ?? 0), 0);
  const rowPlan = excelRowPlanQuantity(row).value ?? 0;
  const allocated = (result: QuantityResult): QuantityResult => result.value === null ? result
    : result.value < 0 ? { value: null, unit: null, reason: "Ortak iş emrinin negatif net miktarı satırlara dağıtılamadı" } : {
    value: Math.max(0, Math.min(rowPlan, result.value - preceding))
      + (index === rows.length - 1 ? Math.max(0, result.value - preceding - rowPlan) : 0),
    unit: result.unit, reason: null,
  };
  const current = allocated(actualQuantity(snapshot, row, day, live, "week"));
  const previousDay = new Date(`${day}T12:00:00Z`);
  previousDay.setUTCDate(previousDay.getUTCDate() - 1);
  const previous = previousDay.toISOString().slice(0, 10) < snapshot.weekStart
    ? { value: 0, unit: "KOLİ", reason: null }
    : allocated(actualQuantity(snapshot, row, previousDay.toISOString().slice(0, 10), live, "week"));
  return {
    daily: day > (live?.range.to ?? "") ? actualQuantity(snapshot, row, day, live, "day")
      : current.value === null || previous.value === null ? { value: null, unit: null, reason: current.reason ?? previous.reason }
      : { value: current.value - previous.value, unit: "KOLİ", reason: null },
    cumulative: allocated(actualQuantity(snapshot, row, weekObservationDate(snapshot, live), live, "week")),
  };
}

/** Prefer the production day's plan before filling another source row on the same physical line. */
function productionDayActuals(snapshot: Week39PackagingSnapshot, row: PackagingPlanRow, rows: PackagingPlanRow[], index: number,
  day: string, live: Week39PackagingLive | null): { daily: QuantityResult; cumulative: QuantityResult } {
  const totals = rows.map(() => 0);
  let daily: QuantityResult = actualQuantity(snapshot, row, day, live, "day");
  const observation = weekObservationDate(snapshot, live);
  const coverage = actualQuantity(snapshot, row, observation, live, "week");
  if (coverage.value === null) return { daily, cumulative: coverage };
  const dates = [...new Set(live?.items.filter((item) => item.date >= snapshot.weekStart && item.date <= observation)
    .map((item) => item.date) ?? [])].sort();
  for (const date of dates) {
    const actual = actualQuantity(snapshot, row, date, live, "day");
    if (actual.value === null || actual.value < 0) {
      const invalid = actual.value === null ? actual : { value: null, unit: null, reason: "Ortak iş emrinin negatif net miktarı satırlara dağıtılamadı" };
      return { daily: date === day ? invalid : daily, cumulative: invalid };
    }
    const priority = rows.map((plan, position) => ({ position, plannedToday: displayPlanAllocations(plan).some((a) => a.date === date) }))
      .sort((a, b) => Number(b.plannedToday) - Number(a.plannedToday) || a.position - b.position);
    let remaining = actual.value;
    let assignedToday = 0;
    for (const { position } of priority) {
      const capacity = Math.max(0, (excelRowPlanQuantity(rows[position]).value ?? 0) - totals[position]);
      const amount = Math.min(capacity, remaining);
      totals[position] += amount; remaining -= amount;
      if (position === index) assignedToday += amount;
    }
    const overflow = priority[0]?.position ?? index;
    totals[overflow] += remaining;
    if (overflow === index) assignedToday += remaining;
    if (date === day) daily = { value: assignedToday, unit: "KOLİ", reason: null };
  }
  return { daily, cumulative: { value: totals[index], unit: "KOLİ", reason: null } };
}
